export function getMeta(name: string): string | undefined {
    const tagSelector = `meta[name="${name}"]`;
    const tag = document.querySelector(tagSelector) as
        | HTMLMetaElement
        | undefined;
    return tag?.content ?? undefined;
}

const trueStrings = ['true', 't', '1', 'on', 'yes', 'y', 'ja'];

function normalizeKey(str: string): string {
    return str.toLowerCase().replaceAll(/[-_.\w'"]/gi, '');
}

function isAllowedValue(
    value: string,
    allowedValue: string | number | bigint | boolean | symbol,
    strict: boolean
): boolean {
    switch (typeof allowedValue) {
        case 'string': {
            if (strict) {
                return value === allowedValue;
            } else {
                return normalizeKey(value) === normalizeKey(allowedValue);
            }
        }
        case 'number':
            return Number(value) === allowedValue;
        case 'bigint':
            return BigInt(value) === allowedValue;
        case 'boolean':
            return allowedValue === trueStrings.includes(value);
        case 'symbol':
            return value === allowedValue.description;
        case 'undefined':
        case 'object':
        case 'function':
            return false;
    }
}

export function getMetaIn<
    A extends string | number | bigint | boolean | symbol
>(name: string, allow: A[], strict = false): A | undefined {
    const str = getMeta(name);
    if (!str) return undefined;

    for (const allowedValue of allow) {
        if (isAllowedValue(str, allowedValue, strict)) return allowedValue;
    }
    console.warn(
        `Meta-tag value with name="${name}" and content="${str}" does not match any allowed value:`,
        allow
    );
    return undefined;
}

export function getMetaMatch<T extends Record<string | number, unknown>>(
    name: string,
    match: T,
    strict = false
): T[keyof T] | undefined {
    const keys = Object.keys(match);
    const key = getMetaIn<keyof T>(name, keys, strict);
    if (key === undefined) return undefined;
    return match[key];
}

export function getMetaBool(name: string): boolean {
    const result = getMeta(name);
    if (result && trueStrings.includes(result.toLowerCase())) return true;
    return false;
}

export function getMetaOrFail(name: string): string {
    const result = getMeta(name);
    if (!result) throw new Error(`Metatag with name ${name} not defined!`);
    return result;
}

export function getMetaNumber(name: string): number | undefined {
    const str = getMeta(name);
    if (str === undefined) return undefined;
    return Number(str);
}

export type Json =
    | boolean
    | number
    | string
    | null
    | { [key: string]: Json }
    | Array<Json>;

export function getMetaJson<T extends Json>(
    name: string,
    check: (v: Json) => v is T
): T | undefined;
export function getMetaJson(
    name: string,
    check?: (v: Json) => boolean
): Json | undefined;
export function getMetaJson(
    name: string,
    check?: (v: Json) => boolean
): Json | undefined {
    const str = getMeta(name);
    if (str === undefined) return undefined;
    const result = JSON.parse(str);
    if (check && !check(result)) return undefined;
    return result;
}
